/**
 * Shared navigation helpers for portal chrome (sidebar, account menu,
 * mobile footer navbar, preferences page). Single home for the route
 * matching and label translation rules so the surfaces never drift.
 */
import type { LayoutItem } from "../types/layout";

type TranslateFn = (key: string) => string;
type TranslationExistsFn = (key: string) => boolean;

/** Whether `route` is the current URL or an ancestor of it (nested routes). */
export function isNavRouteActive(currentUrl: string, route: string): boolean {
    return currentUrl === route || currentUrl.startsWith(route + "/");
}

/**
 * Check if a route is internal to the current portal.
 * External routes should use <a> tags to force full page reload,
 * preventing Inertia from trying to resolve pages that don't exist in this
 * portal's bundle. Items with `inertia: true` are always treated as internal
 * (set by packages that register their own pages in the current portal's
 * bundle).
 */
export function isInternalPortalRoute(
    routePrefix: string,
    route: string,
    item?: Pick<LayoutItem, "inertia">,
): boolean {
    if (item?.inertia) {
        return true;
    }
    // If prefix is empty (root portal like Employee Portal), only '/' is
    // internal — everything else like '/crud/*', '/auth/*' is external
    if (routePrefix === "") {
        return route === "/" || route.startsWith("/?");
    }
    return (
        route === routePrefix ||
        route.startsWith(routePrefix + "/") ||
        route.startsWith(routePrefix + "?")
    );
}

/**
 * Translate a nav item label: model translation prefix first
 * (e.g. "models.user.plural"), then the label as a direct translation key,
 * then the raw label.
 */
export function translateNavLabel(
    translate: TranslateFn,
    translationExists: TranslationExistsFn,
    item: Pick<LayoutItem, "label" | "translationPrefix">,
    type: "singular" | "plural" = "plural",
): string {
    if (item.translationPrefix) {
        const key = `models.${item.translationPrefix}.${type}`;
        if (translationExists(key)) {
            return translate(key);
        }
    }
    if (item.label && translationExists(item.label)) {
        return translate(item.label);
    }
    return item.label;
}
